import React from "react";
import { makeStyles } from "@material-ui/core/styles";

import { T } from "../../constants";
import { cx } from "../../ui";
import Tag from "../../components/Tag";

const useStyles = makeStyles({
  list: {
    borderRadius: 6,
    border: `1px solid ${T.border}`,
    background: T.elevated,
    maxHeight: 180,
    overflowY: "auto",
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "7px 10px",
    fontSize: 13,
    color: T.fg,
    cursor: "pointer",
    borderBottom: `1px solid ${T.borderSoft}`,
    "&:last-child": { borderBottom: "none" },
    "&:hover": { background: T.surface },
  },
  rowDisabled: {
    cursor: "not-allowed",
    color: T.mutedFg,
    "&:hover": { background: "transparent" },
  },
  checkbox: { width: 15, height: 15, accentColor: T.primary, flexShrink: 0 },
  name: { flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  channels: { fontSize: 11, color: T.mutedFg, flexShrink: 0 },
  channelsNone: { color: T.warningFg },
  empty: { margin: 0, padding: "10px 12px", fontSize: 12, color: T.mutedFg },
});

export function contactChannels(contact) {
  const sms = Boolean(contact.phone?.trim());
  const email = Boolean(contact.email?.trim());

  if (sms && email) return "SMS + email";
  if (sms) return "SMS only";
  if (email) return "email only";

  return "no channel";
}

export const isReachable = (contact) =>
  Boolean(contact.phone?.trim() || contact.email?.trim());

function VipContactPicker({
  contacts,
  value = [],
  onChange,
  pagedIds = [],
  disabled = false,
  requireReachable = true,
  showChannels = true,
  emptyMessage = "No contacts are configured yet.",
}) {
  const s = useStyles();

  const toggle = (id) =>
    onChange(value.includes(id) ? value.filter((v) => v !== id) : [...value, id]);

  const selected = new Set([...value, ...pagedIds]);

  return (
    <>
      <div className={s.list}>
        {contacts.length === 0 ? (
          <p className={s.empty}>{emptyMessage}</p>
        ) : (
          contacts.map((c) => {
            const paged = pagedIds.includes(c.id);
            const reachable = isReachable(c);
            const rowDisabled = disabled || paged || (requireReachable && !reachable);

            return (
              <label
                key={c.id}
                className={cx(s.row, rowDisabled && s.rowDisabled)}
                title={!requireReachable || reachable ? undefined : "No phone or email on this contact"}
              >
                <input
                  type="checkbox"
                  className={s.checkbox}
                  checked={selected.has(c.id)}
                  disabled={rowDisabled}
                  onChange={() => toggle(c.id)}
                />
                <span className={s.name}>{c.name}</span>
                {paged && <Tag tone="ok" size={10} upper>paged</Tag>}
                {showChannels && (
                  <span className={cx(s.channels, !reachable && s.channelsNone)}>
                    {contactChannels(c)}
                  </span>
                )}
              </label>
            );
          })
        )}
      </div>
    </>
  );
}

export default VipContactPicker;
